/**
 * People search, backed either by Supabase Postgres (the original ~72k rows)
 * or by the ClickHouse search API on the VPS (the 146M catalog).
 *
 * Which one runs is controlled by REACT_APP_SEARCH_API_URL:
 *   unset  -> Supabase RPC, exactly as before
 *   set    -> ClickHouse API
 *
 * Keeping both behind one function means Research.jsx does not care where
 * rows come from, and we can fall back instantly if the VPS has a problem.
 *
 * IMPORTANT SPLIT: ClickHouse serves the catalog, but it knows nothing about
 * who has paid for what. Reveal state lives in Supabase (`reveals`), so after
 * fetching a page we look up just those person_ids and merge the flags in.
 * The two systems only ever share person_id.
 */
import { supabase } from "@/lib/supabase";

const SEARCH_API_URL = process.env.REACT_APP_SEARCH_API_URL || "";

export const usingClickHouse = () => Boolean(SEARCH_API_URL);

/** Map the UI's filter state onto the search API's payload. */
function toApiFilters(pf, { limit, offset }) {
  return {
    query: pf.query || null,
    name: pf.name || null,
    title: pf.title || null,
    title_exclude: pf.title_exclude || null,
    seniority: pf.seniority || [],
    industry: pf.industry || [],
    company_size: pf.size || [],
    revenue_band: pf.revenue_band || [],
    company_name: pf.company_name || null,
    company_domain: pf.company_domain || null,
    // The UI has one "location" box plus explicit city/state/country.
    city: pf.city || pf.location || null,
    state: pf.state || null,
    country: pf.country || null,
    employee_min: pf.employee_min !== "" ? parseInt(pf.employee_min, 10) : null,
    employee_max: pf.employee_max !== "" ? parseInt(pf.employee_max, 10) : null,
    has_phone: !!pf.has_phone,
    has_email: !!pf.has_email,
    limit,
    offset,
  };
}

/**
 * Merge Supabase reveal state onto rows returned by ClickHouse.
 * One query per page, keyed by the ids actually on screen.
 *
 * Only status='confirmed' counts. A reveal that was charged but never
 * confirmed sits 'pending'; treating that as revealed was actively harmful,
 * because the hydrate path DOES filter on 'confirmed' and returns no value for
 * it. The row rendered as revealed, displayed masked text where the paid value
 * belongs, and hid the reveal button — removing the one action that would have
 * fixed it. Reserving again on a pending row returns the same reservation at
 * price 0 and re-fetches, so leaving the button in place lets the user heal the
 * leak themselves, for free, without waiting for the sweeper.
 */
async function attachRevealState(rows) {
  if (!rows.length) return rows;
  const ids = rows.map((r) => r.id);

  const { data, error } = await supabase
    .from("reveals")
    .select("person_id, reveal_type")
    .eq("status", "confirmed")
    .in("person_id", ids);

  if (error) {
    // Reveal state is additive: without it every row simply renders as
    // un-revealed, which is safe (it can never leak a value).
    console.warn("reveal lookup failed:", error.message);
    return rows;
  }

  const email = new Set();
  const phone = new Set();
  const companyPhone = new Set();
  for (const r of data || []) {
    if (r.reveal_type === "email") email.add(r.person_id);
    else if (r.reveal_type === "phone") phone.add(r.person_id);
    else if (r.reveal_type === "company_phone") companyPhone.add(r.person_id);
  }

  return rows.map((r) => ({
    ...r,
    email_revealed: email.has(r.id),
    phone_revealed: phone.has(r.id),
    company_phone_revealed: companyPhone.has(r.id),
  }));
}

/**
 * Job-title suggestions, ordered by how many people hold each title.
 * Returns [] rather than throwing — an autocomplete that errors is worse
 * than one that quietly shows nothing.
 */
export async function suggestTitles(q, limit = 8) {
  if (!SEARCH_API_URL || !q || q.trim().length < 2) return [];
  try {
    const { data } = await supabase.auth.getSession();
    const token = data?.session?.access_token;
    if (!token) return [];
    const res = await fetch(`${SEARCH_API_URL}/suggest/titles`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ q, limit }),
    });
    if (!res.ok) return [];
    return (await res.json()).suggestions || [];
  } catch {
    return [];
  }
}

/**
 * The values the catalog's filterable columns actually contain.
 *
 * The dropdowns used to ship a hardcoded vocabulary written for the original
 * 72k Supabase dataset; against the 124.9M catalog only 4 of 170 industry
 * options existed at all, and the size and revenue lists reached 0.07% and
 * 0.03% of the rows that have a value. Serving the vocabulary from the data is
 * the only version that cannot drift.
 *
 * Returns null when the catalog is off, so the caller keeps its static lists.
 * Cached for the tab's lifetime — this is a per-bulk-load constant.
 */
let _facetsPromise = null;
export function fetchFacets() {
  if (!SEARCH_API_URL) return Promise.resolve(null);
  if (_facetsPromise) return _facetsPromise;
  _facetsPromise = (async () => {
    try {
      const { data } = await supabase.auth.getSession();
      const token = data?.session?.access_token;
      if (!token) return null;
      const res = await fetch(`${SEARCH_API_URL}/facets`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return null;
      return await res.json();
    } catch {
      // Falling back to the static lists is worse but not broken.
      _facetsPromise = null;
      return null;
    }
  })();
  return _facetsPromise;
}

/**
 * Fill in values the user has already paid for.
 *
 * The catalog returns only masked values — it has no idea who revealed what.
 * Without this a revealed email reloads as "ab***@x.com": masked text sitting
 * exactly where the paid value belongs. The Edge Function owns the reveals
 * table, so it decides which ids qualify; this never charges.
 *
 * One request per field, and only when some row is actually flagged revealed.
 */
async function hydrateRevealed(rows, token) {
  const wanted = [
    ["email", rows.filter((r) => r.email_revealed).map((r) => r.id)],
    ["phone", rows.filter((r) => r.phone_revealed).map((r) => r.id)],
    ["company_phone", rows.filter((r) => r.company_phone_revealed).map((r) => r.id)],
  ].filter(([, ids]) => ids.length > 0);

  if (wanted.length === 0) return rows;

  const results = await Promise.all(
    wanted.map(async ([field, ids]) => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/reveal-contact`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify({ person_ids: ids, field }),
          },
        );
        if (!res.ok) return [field, {}];
        return [field, (await res.json()).values || {}];
      } catch {
        // Non-fatal: the row keeps its masked value rather than breaking the page.
        return [field, {}];
      }
    }),
  );

  const byField = Object.fromEntries(results);
  return rows.map((r) => {
    const patch = {};
    const em = byField.email?.[r.id];
    if (em) { patch.email = em; patch.email_masked = em; }
    const ph = byField.phone?.[r.id];
    if (ph) patch.mobile_phone = ph;
    const cp = byField.company_phone?.[r.id];
    if (cp) patch.company_phone = cp;
    return Object.keys(patch).length ? { ...r, ...patch } : r;
  });
}

/**
 * Members of a saved list.
 *
 * Membership lives in Supabase (list_contacts) but the people live in the
 * catalog, so this reads the ids from Postgres and hydrates them from the
 * search API. When the catalog is off, it falls back to the list_members()
 * RPC, which joins public.people directly.
 *
 * @returns {Promise<object[]>} rows shaped like list_members() output
 */
export async function fetchListMembers(listId) {
  if (!SEARCH_API_URL) {
    const { data, error } = await supabase.rpc("list_members", { p_list_id: listId });
    if (error) throw new Error(error.message);
    return data || [];
  }

  const { data: memberships, error } = await supabase
    .from("list_contacts")
    .select("person_id, company_id, entity_type, added_at")
    .eq("list_id", listId);
  if (error) throw new Error(error.message);

  const personIds = (memberships || []).map((m) => m.person_id).filter(Boolean);
  if (personIds.length === 0) return [];

  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData?.session?.access_token;
  if (!token) throw new Error("Not signed in");

  const res = await fetch(`${SEARCH_API_URL}/people/by_ids`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify({ ids: personIds }),
  });
  if (!res.ok) throw new Error(`Could not load list members (${res.status})`);

  const byId = new Map(((await res.json()).rows || []).map((r) => [r.id, r]));
  const withReveals = await hydrateRevealed(
    await attachRevealState([...byId.values()]), token,
  );
  const hydrated = new Map(withReveals.map((r) => [r.id, r]));
  const addedAt = new Map((memberships || []).map((m) => [m.person_id, m.added_at]));

  // Shape to what ListDetail expects from list_members(). A membership whose
  // person is missing from the catalog is dropped rather than rendered blank.
  return personIds
    .filter((id) => hydrated.has(id))
    .map((id) => {
      const r = hydrated.get(id);
      return {
        entity_type: "person",
        list_id: listId,
        person_id: r.id,
        company_id: null,
        name: r.full_name,
        title: r.title,
        seniority: r.seniority,
        email: r.email_revealed ? r.email : "",
        has_email: r.has_email,
        email_revealed: r.email_revealed,
        email_status: r.email_status,
        mobile_phone: r.mobile_phone || "",
        work_direct_phone: "",
        work_direct_phone_2: "",
        home_phone: "",
        corporate_phone: "",
        has_phone: r.has_phone,
        phone_revealed: r.phone_revealed,
        location: r.person_location,
        linkedin_url: r.linkedin_url,
        domain: r.company_domain,
        industry: r.company_industry,
        size: r.company_size,
        employee_count: r.company_employee_count,
        website: r.company_website,
        company_phone: r.company_phone,
        has_company_phone: r.has_company_phone,
        company_phone_revealed: r.company_phone_revealed,
        added_at: addedAt.get(id) || null,
      };
    })
    .sort((a, b) => String(b.added_at || "").localeCompare(String(a.added_at || "")));
}

/**
 * Reveal one contact field.
 *
 * Supabase-backed rows keep the original single-transaction RPCs, which can
 * charge and read the value atomically because both live in Postgres.
 *
 * Catalog rows go through the reveal-contact Edge Function instead: the value
 * lives on the ClickHouse VPS, so the credit is reserved first, the value
 * fetched over the network, then the charge is confirmed — or refunded if
 * nothing came back. Charge-on-success is preserved either way.
 *
 * @returns {Promise<{ok: boolean, error?: string, patch?: object,
 *                    creditsSpent: number, alreadyRevealed: boolean}>}
 */
export async function revealContact(personId, type) {
  if (!SEARCH_API_URL) {
    const rpc = type === "email" ? "reveal_person_email" : "reveal_person_phone";
    const { data, error } = await supabase.rpc(rpc, { p_person_id: personId });
    if (error) return { ok: false, error: error.message, creditsSpent: 0, alreadyRevealed: false };
    if (data?.error) return { ok: false, error: data.error, ...data, creditsSpent: 0, alreadyRevealed: false };
    const patch = type === "email"
      ? { email_revealed: true, email: data.email, email_masked: data.email, secondary_email: data.secondary_email }
      : {
          phone_revealed: true, mobile_phone: data.mobile_phone,
          work_direct_phone: data.work_direct_phone, work_direct_phone_2: data.work_direct_phone_2,
          home_phone: data.home_phone, corporate_phone: data.corporate_phone,
        };
    return { ok: true, patch, creditsSpent: data.already_revealed ? 0 : 1, alreadyRevealed: !!data.already_revealed };
  }

  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData?.session?.access_token;
  if (!token) return { ok: false, error: "not_authenticated", creditsSpent: 0, alreadyRevealed: false };

  const res = await fetch(
    `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/reveal-contact`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ person_id: personId, field: type }),
    },
  );

  let payload;
  try {
    payload = await res.json();
  } catch {
    return { ok: false, error: "bad_response", creditsSpent: 0, alreadyRevealed: false };
  }

  if (payload.error && !payload.found) {
    return { ok: false, ...payload, creditsSpent: 0, alreadyRevealed: false };
  }
  if (!payload.found) {
    return { ok: false, error: "no_value", creditsSpent: 0, alreadyRevealed: false };
  }

  // The catalog has no personal phone numbers yet (the source "Phone" column
  // is the company switchboard), so a phone reveal fills mobile_phone only
  // once a provider supplies one.
  const patch =
    type === "email"
      ? { email_revealed: true, email: payload.value, email_masked: payload.value }
      : type === "company_phone"
        ? { company_phone_revealed: true, company_phone: payload.value }
        : { phone_revealed: true, mobile_phone: payload.value };

  return {
    ok: true,
    patch,
    creditsSpent: payload.credits_charged ?? 0,
    alreadyRevealed: !!payload.already_revealed,
  };
}

/**
 * @returns {Promise<{rows: object[], total: number, totalIsCapped: boolean}>}
 */
export async function searchPeople(pf, { limit, offset, supabaseParams, onHydrated }) {
  if (!SEARCH_API_URL) {
    const countParams = { ...supabaseParams };
    delete countParams.p_limit;
    delete countParams.p_offset;
    const [dataRes, countRes] = await Promise.all([
      supabase.rpc("search_people", supabaseParams),
      supabase.rpc("count_people", countParams),
    ]);
    if (dataRes.error) throw new Error(dataRes.error.message);
    return {
      rows: (dataRes.data || []).map((r) => ({ ...r, email: r.email_masked })),
      total: Number(countRes.data) || 0,
      totalIsCapped: false,
    };
  }

  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData?.session?.access_token;
  if (!token) throw new Error("Not signed in");

  const res = await fetch(`${SEARCH_API_URL}/search`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(toApiFilters(pf, { limit, offset })),
  });

  if (!res.ok) {
    const detail = await res.text().catch(() => "");
    throw new Error(`Search failed (${res.status}) ${detail.slice(0, 120)}`);
  }

  const payload = await res.json();
  // `email` mirrors the masked value so the table renders identically to the
  // Supabase path, where search_people() already returns email_masked.
  const base = (payload.rows || []).map((r) => ({ ...r, email: r.email_masked }));

  // Reveal state is two more round trips: a Supabase lookup, then the
  // reveal-contact Edge Function, which can cold-start for seconds. Awaiting
  // them here made the whole table wait on data that affects a handful of
  // cells — the search itself is ~10ms. They now arrive as a later patch, so
  // rows paint as soon as the catalog answers.
  if (onHydrated) {
    (async () => {
      try {
        const full = await hydrateRevealed(await attachRevealState(base), token);
        onHydrated(full.map((r) => ({ ...r, email: r.email_masked })));
      } catch (e) {
        // Non-fatal: rows simply stay un-revealed, which can never leak a value.
        console.warn("reveal hydration failed:", e.message);
      }
    })();
  }

  return {
    rows: base,
    total: payload.total || 0,
    totalIsCapped: Boolean(payload.total_is_capped),
  };
}
